import React from "react";
import PropTypes from "prop-types";
import MultiStyleButton from "../../../../systemUtils/MultiStyleButton";

const UserStep2AddUsers = ({
  setCardStep,
  companyStructure,
  userSystem,
  onChange,
  handleCheckboxChange,
  handleRoleCheckboxChange,
  getRoleKey,
}) => {
  // ---------------------------
  // 1) Group-Level Table
  // ---------------------------
  const renderTableConfig = () => {
    if (!companyStructure?.userGroups) {
      return <p>No user groups found. Cannot build config.</p>;
    }

    const groups = companyStructure.userGroups;
    return (
      <div className="custom-config-section">
        <h4 className="table-title">Add Permission per User Group</h4>
        <p className="table-explainer">
          Each row displays a “target” group (the group that will receive new
          users), while each column shows a “source” group (the group allowed to
          add users into that target). You can give permission by checking the
          box at each intersection.
        </p>

        <table className="permissions-table">
          <thead>
            <tr>
              <th>Target Group</th>
              {groups.map((g) => (
                <th key={g.groupName}>{g.groupName}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((targetG) => {
              const targetName = targetG.groupName;
              const allowedSources = userSystem.addUserConfig[targetName] || [];

              return (
                <tr key={targetName}>
                  <td>{targetName}</td>
                  {groups.map((sourceG) => {
                    const sourceName = sourceG.groupName;
                    const isChecked = allowedSources.includes(sourceName);

                    return (
                      <td key={sourceName}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() =>
                            handleCheckboxChange(targetName, sourceName)
                          }
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ---------------------------
  // 2) Role-Level Table
  // ---------------------------
  const renderRoleLevelTable = () => {
    if (!companyStructure?.userGroups) {
      return <p>No user groups found. Cannot build role-level config.</p>;
    }

    // Flatten all (group, role) combos
    const allRoles = companyStructure.userGroups.flatMap((g) =>
      g.roles.map((r) => ({
        groupName: g.groupName,
        roleName: r.roleName,
      }))
    );

    if (allRoles.length === 0) {
      return <p>No roles found in the organization.</p>;
    }

    return (
      <div className="custom-config-section">
        <h4 className="table-title">Add Permission per User Role</h4>
        <p className="table-explainer">
          Each row displays a “target” role (the role that will receive new
          users), while each column shows a “source” role (the role allowed to
          add users into that target).
        </p>

        <table className="permissions-table">
          <thead>
            <tr>
              <th>Target Role</th>
              {allRoles.map((sourceObj) => {
                const colKey = getRoleKey(
                  sourceObj.groupName,
                  sourceObj.roleName
                );
                return (
                  <th key={colKey}>
                    {sourceObj.groupName} - {sourceObj.roleName}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {allRoles.map((targetObj) => {
              const targetKey = getRoleKey(
                targetObj.groupName,
                targetObj.roleName
              );

              return (
                <tr key={targetKey}>
                  <td>
                    {targetObj.groupName} - {targetObj.roleName}
                  </td>
                  {allRoles.map((sourceObj) => {
                    const sourceKey = getRoleKey(
                      sourceObj.groupName,
                      sourceObj.roleName
                    );
                    const allowedSources =
                      userSystem.addRoleConfig?.[targetKey] || [];
                    const isChecked = allowedSources.includes(sourceKey);

                    return (
                      <td key={sourceKey}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() =>
                            handleRoleCheckboxChange(targetKey, sourceKey)
                          }
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ---------------------------
  // 3) Toggle between group vs role config
  // ---------------------------
  const handleRoleLevelCheckbox = (e) => {
    const newValue = e.target.checked;
    // If user toggles on => clear group-level
    // If toggles off => clear role-level
    const newUserSystem = { ...userSystem };

    if (newValue) {
      newUserSystem.allowRoleLevelConfig = true;
      newUserSystem.addUserConfig = {};
    } else {
      newUserSystem.allowRoleLevelConfig = false;
      newUserSystem.addRoleConfig = {};
    }

    onChange(newUserSystem);
  };

  // ---------------------------
  // 4) Validation on “Continue”
  // ---------------------------
  const handleContinue = () => {
    if (!companyStructure?.userGroups) {
      // If no groups, skip validation or handle differently
      setCardStep(3);
      return;
    }

    if (!userSystem.allowRoleLevelConfig) {
      // GROUP-LEVEL mode
      // For each group, we need at least one source group
      const missingGroups = [];
      for (const g of companyStructure.userGroups) {
        const targetName = g.groupName;
        const assigned = userSystem.addUserConfig[targetName] || [];
        if (assigned.length === 0) {
          // means no source group assigned => invalid
          missingGroups.push(targetName);
        }
      }
      if (missingGroups.length > 0) {
        alert(
          `Please assign at least one source group for these target groups: ${missingGroups.join(
            ", "
          )}`
        );
        return; // stop
      }
    } else {
      // ROLE-LEVEL mode
      // For each role, we need at least one source role
      const allRoles = companyStructure.userGroups.flatMap((g) =>
        g.roles.map((r) => ({
          groupName: g.groupName,
          roleName: r.roleName,
        }))
      );

      const missingRoles = [];
      for (const roleObj of allRoles) {
        const targetKey = getRoleKey(roleObj.groupName, roleObj.roleName);
        const assigned = userSystem.addRoleConfig?.[targetKey] || [];
        if (assigned.length === 0) {
          missingRoles.push(`${roleObj.groupName} - ${roleObj.roleName}`);
        }
      }
      if (missingRoles.length > 0) {
        alert(
          `Please assign at least one source role for these target roles: ${missingRoles.join(
            ", "
          )}`
        );
        return; // stop
      }
    }

    // If we reach here, validation passed => proceed
    setCardStep(3);
  };

  return (
    <>
      <h2 className="card-title">Add New Users</h2>
      <p className="card-subtitle">
        Control which user groups or roles can add new users at each level of
        your organization.
      </p>

      {/* Role-level config checkbox */}
      <div className="role-toggle-row">
        <label>
          <input
            type="checkbox"
            checked={userSystem.allowRoleLevelConfig || false}
            onChange={handleRoleLevelCheckbox}
          />
          Enable Role-Level Config?
        </label>
      </div>

      {userSystem.allowRoleLevelConfig ? (
        <div className="advanced-role-config">{renderRoleLevelTable()}</div>
      ) : (
        renderTableConfig()
      )}

      <div className="continue-button-container">
        <MultiStyleButton
          label="Back"
          buttonType="cancel"
          onClick={() => setCardStep(1)}
        />
        <MultiStyleButton
          label="Continue"
          buttonType="approve"
          onClick={handleContinue}
        />
      </div>
    </>
  );
};

UserStep2AddUsers.propTypes = {
  setCardStep: PropTypes.func.isRequired,
  companyStructure: PropTypes.object,
  userSystem: PropTypes.shape({
    allowRoleLevelConfig: PropTypes.bool,
    addUserConfig: PropTypes.object.isRequired,
    addRoleConfig: PropTypes.object,
  }).isRequired,
  onChange: PropTypes.func.isRequired,

  // The table logic for toggles:
  handleCheckboxChange: PropTypes.func.isRequired,
  handleRoleCheckboxChange: PropTypes.func.isRequired,
  getRoleKey: PropTypes.func.isRequired,
};

export default UserStep2AddUsers;
